import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuill } from 'react-quilljs'
import clsx from 'clsx'
import Const from 'constants/constant'
import CustomBlotFormatter from 'quill/CustomBlotFormatter'
import registerQuillImageHandler from 'utils/quillUtils'

import 'quill/dist/quill.snow.css'

export interface QuillEditorV2Props {
  value?: string
  onChange?: (content: string) => void
  readOnly?: boolean
  placeholder?: string
  className?: string
  height?: number | string
}

/**
 * Check if the HTML content from Quill Editor is truly empty.
 * Handles cases where Quill generates empty HTML tags.
 */
const isQuillEmpty = (htmlContent?: string) => {
  if (!htmlContent) return true

  if (htmlContent === '<p><br></p>' || htmlContent === '<p></p>') {
    return true
  }

  const strippedContent = htmlContent.replace(/<[^>]*>?/gm, '').trim()
  const hasMedia =
    htmlContent.includes('<img') || htmlContent.includes('<iframe')

  return strippedContent.length === 0 && !hasMedia
}

/**
 * Quill sync variant: listens to text-change, editor-change, and root input so
 * parent state updates when images are inserted/resized (not only on text deltas).
 */
function QuillEditorV2({
  value,
  onChange,
  readOnly = false,
  placeholder = '入力してください',
  className,
  height = 250
}: QuillEditorV2Props) {
  const quillModules = useMemo(() => Const.QUILL_MODULES, [])
  const quillFormats = useMemo(() => Const.QUILL_FORMATS, [])
  const lastHtmlRef = useRef<string>('__quill_init__')
  // Caches recent outputs to prevent React uncontrolled rendering loops from breaking ongoing Quill formatting (e.g. dragging images to resize)
  const emittedValuesRef = useRef<Set<string>>(new Set())

  const { t } = useTranslation()

  const { quill, quillRef, Quill } = useQuill({
    theme: 'snow',
    modules: quillModules,
    formats: quillFormats,
    placeholder: isQuillEmpty(value) ? placeholder : ''
  })

  useEffect(() => {
    if (Quill && !quill) {
      if (!Quill.imports['modules/blotFormatter']) {
        Quill.register('modules/blotFormatter', CustomBlotFormatter)
      }
    }
  }, [Quill, quill])

  // Register custom image handler: validates size/resolution,
  // handles toolbar click, paste, and drag-drop.
  useEffect(() => {
    registerQuillImageHandler(quill, t)
  }, [quill, t])

  useEffect(() => {
    if (quill) {
      // Change link placeholder from 'https://quilljs.com' to 'https://'
      // We override the tooltip.edit method because Quill resets the input placeholder
      // every time the link tooltip is shown (e.g. when clicking the link button).
      const { tooltip } = (quill as any).theme
      if (tooltip && !tooltip.isPatched) {
        const originalEdit = tooltip.edit
        tooltip.edit = (mode: string, value: string) => {
          originalEdit.call(tooltip, mode, value)
          if (mode === 'link') {
            const input = tooltip.root.querySelector('input[data-link]')
            if (input) {
              input.setAttribute('placeholder', 'https://')
            }
          }
        }
        tooltip.isPatched = true
      }
      quill.enable(!readOnly)
    }
  }, [quill, readOnly])

  useEffect(() => {
    if (quill) {
      const isEmpty = isQuillEmpty(value)
      const actualPlaceholder = isEmpty ? placeholder : ''
      quill.root.setAttribute('data-placeholder', actualPlaceholder)

      if (!isEmpty) {
        quill.root.classList.remove('ql-blank')
      } else {
        quill.root.classList.add('ql-blank')
      }
    }
  }, [quill, value, placeholder])

  useEffect(() => {
    if (!quill || value === undefined) return

    // Ignore updates that are merely echoing our own emitted HTML.
    if (value === lastHtmlRef.current || emittedValuesRef.current.has(value)) {
      return
    }

    if (value !== quill.root.innerHTML) {
      const isHtmlEmpty = isQuillEmpty(value)
      if (isHtmlEmpty) {
        quill.root.innerHTML = value || ''
      } else if (quill.clipboard?.dangerouslyPasteHTML) {
        // Use clipboard to ensure Quill's internal Delta stays in sync with DOM elements like images
        quill.clipboard.dangerouslyPasteHTML(value || '')
      } else {
        quill.root.innerHTML = value || ''
      }
      lastHtmlRef.current = quill.root.innerHTML
    }
  }, [quill, value])

  useEffect(() => {
    if (!quill || !onChange) return

    const notifyIfHtmlChanged = () => {
      const html = quill.root.innerHTML
      if (html !== lastHtmlRef.current) {
        lastHtmlRef.current = html
        emittedValuesRef.current.add(html)
        if (emittedValuesRef.current.size > 50) {
          const first = emittedValuesRef.current.values().next().value
          if (first !== undefined) {
            emittedValuesRef.current.delete(first)
          }
        }
        onChange(html)
      }
    }

    quill.on('text-change', notifyIfHtmlChanged)
    quill.on('editor-change', notifyIfHtmlChanged)
    quill.root.addEventListener('input', notifyIfHtmlChanged)

    return () => {
      quill.off('text-change', notifyIfHtmlChanged)
      quill.off('editor-change', notifyIfHtmlChanged)
      quill.root.removeEventListener('input', notifyIfHtmlChanged)
    }
  }, [quill, onChange])

  return (
    <div
      style={{ height, marginBottom: 45 }}
      className={clsx('quill-custom-editor', className)}
    >
      <div ref={quillRef} />
    </div>
  )
}

QuillEditorV2.defaultProps = {
  value: '',
  onChange: undefined,
  readOnly: false,
  placeholder: '入力してください',
  className: '',
  height: 250
}

export default React.memo(QuillEditorV2)
